'use client';

import React, { useState } from 'react';
import { X, Clock, Trash2 } from 'lucide-react';
import type { SavedPost, ScheduledEntry } from './SchedulerLayout';

interface Props {
  currentMonth: Date;
  entries: ScheduledEntry[];
  draggingPost: SavedPost | null;
  selectedEntry: ScheduledEntry | null;
  onSelectEntry: (entry: ScheduledEntry | null) => void;
  onSchedule: (post: SavedPost, date: string, time: string) => void;
  onRemoveEntry: (entryId: string) => void;
  onUpdateTime: (entryId: string, time: string) => void;
  onDragEnd: () => void;
}

const platformColors: Record<string, string> = {
  LI: '#0A66C2',
  IG: '#C13584',
  X: '#aaaaaa',
  FB: '#1877F2',
  BLOG: '#FF5F1F',
  TT: '#69C9D0',
};

const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function getDaysInMonth(year: number, month: number): Date[] {
  const days: Date[] = [];
  const firstDay = new Date(year, month, 1);
  // Monday-based week: shift so Monday = 0
  let startDow = firstDay.getDay(); // 0=Sun,1=Mon,...
  startDow = startDow === 0 ? 6 : startDow - 1;
  // Fill leading empty days
  for (let i = 0; i < startDow; i++) {
    let d = new Date(year, month, 1 - (startDow - i));
    days.push(d);
  }
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(new Date(year, month, d));
  }
  // Fill trailing days to complete last row
  const remaining = 7 - (days.length % 7);
  if (remaining < 7) {
    for (let i = 1; i <= remaining; i++) {
      days.push(new Date(year, month + 1, i));
    }
  }
  return days;
}

function toDateStr(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

interface TimePickerModalProps {
  post: SavedPost;
  date: string;
  onConfirm: (time: string) => void;
  onCancel: () => void;
}

function TimePickerModal({ post, date, onConfirm, onCancel }: TimePickerModalProps) {
  const [time, setTime] = useState('09:00');

  const platformLabel: Record<string, string> = {
    LI: 'LinkedIn', IG: 'Instagram', X: 'X (Twitter)', FB: 'Facebook', BLOG: 'Blog', TT: 'TikTok',
  };

  const dateObj = new Date(date + 'T12:00:00');
  const dateFormatted = dateObj.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onCancel}>
      <div
        className="punk-card w-80 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <p className="section-label">Veröffentlichungszeit</p>
          <button onClick={onCancel} className="text-muted hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>

        <div className="mb-4 p-3 bg-surface border border-border rounded">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="mono text-xs font-700"
              style={{ color: platformColors[post.platform] ?? '#666' }}
            >
              {post.platform}
            </span>
            <span className="text-xs text-muted">{platformLabel[post.platform] ?? post.platform}</span>
          </div>
          <p className="text-xs font-500 text-white leading-snug line-clamp-2">{post.title}</p>
        </div>

        <div className="mb-4">
          <p className="text-xs text-muted mb-1">{dateFormatted}</p>
          <label className="label-punk">Uhrzeit</label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="input-punk text-sm"
          />
        </div>

        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-ghost flex-1 py-2 text-xs">
            Abbrechen
          </button>
          <button
            onClick={() => onConfirm(time)}
            className="btn-primary flex-1 py-2 text-xs"
          >
            Einplanen
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SchedulerCalendar({
  currentMonth,
  entries,
  draggingPost,
  selectedEntry,
  onSelectEntry,
  onSchedule,
  onRemoveEntry,
  onUpdateTime,
  onDragEnd,
}: Props) {
  const [dropTargetDate, setDropTargetDate] = useState<string | null>(null);
  const [pendingDrop, setPendingDrop] = useState<{ post: SavedPost; date: string } | null>(null);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const days = getDaysInMonth(year, month);
  const today = toDateStr(new Date());

  const entriesByDate: Record<string, ScheduledEntry[]> = {};
  entries.forEach((e) => {
    if (!entriesByDate[e.date]) entriesByDate[e.date] = [];
    entriesByDate[e.date].push(e);
  });

  const handleDragOver = (e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    setDropTargetDate(dateStr);
  };

  const handleDrop = (e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    setDropTargetDate(null);
    onDragEnd();
    if (draggingPost) {
      setPendingDrop({ post: draggingPost, date: dateStr });
    }
  };

  const handleDragLeave = () => {
    setDropTargetDate(null);
  };

  const handleTimeConfirm = (time: string) => {
    if (pendingDrop) {
      onSchedule(pendingDrop.post, pendingDrop.date, time);
      setPendingDrop(null);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-background">
      {/* Day labels */}
      <div className="grid grid-cols-7 border-b border-border flex-shrink-0">
        {DAY_LABELS.map((label) => (
          <div key={label} className="py-2 text-center">
            <span className="section-label">{label}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-7 h-full" style={{ gridAutoRows: 'minmax(100px, 1fr)' }}>
          {days.map((day, idx) => {
            const dateStr = toDateStr(day);
            const isCurrentMonth = day.getMonth() === month;
            const isToday = dateStr === today;
            const isDropTarget = dropTargetDate === dateStr && draggingPost !== null;
            const dayEntries = entriesByDate[dateStr] ?? [];

            return (
              <div
                key={`day-${idx}`}
                className={`border-b border-r border-border p-1.5 transition-all duration-100 ${
                  !isCurrentMonth ? 'opacity-30' : ''
                } ${isDropTarget ? 'bg-accent/8 border-accent/40' : 'hover:bg-surface/50'}`}
                onDragOver={(e) => handleDragOver(e, dateStr)}
                onDrop={(e) => handleDrop(e, dateStr)}
                onDragLeave={handleDragLeave}
              >
                {/* Day number */}
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`mono text-xs font-600 w-6 h-6 flex items-center justify-center rounded ${
                      isToday
                        ? 'bg-accent text-black' :'text-muted'
                    }`}
                  >
                    {day.getDate()}
                  </span>
                  {isDropTarget && (
                    <span className="text-xs text-accent mono">+</span>
                  )}
                </div>

                {/* Entries */}
                <div className="space-y-0.5">
                  {dayEntries.slice(0, 3).map((entry) => (
                    <button
                      key={entry.id}
                      onClick={() => onSelectEntry(selectedEntry?.id === entry.id ? null : entry)}
                      className={`w-full text-left px-1.5 py-1 rounded transition-all group ${
                        selectedEntry?.id === entry.id
                          ? 'bg-accent/15 border border-accent/40' :'bg-surface border border-border hover:border-accent/30'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ background: platformColors[entry.platform] ?? '#666' }}
                        />
                        <span
                          className="mono text-xs font-700 flex-shrink-0"
                          style={{ color: platformColors[entry.platform] ?? '#666', fontSize: '9px' }}
                        >
                          {entry.platform}
                        </span>
                        <span className="text-xs text-muted mono flex-shrink-0" style={{ fontSize: '9px' }}>
                          {entry.time}
                        </span>
                      </div>
                      <p className="text-white leading-tight mt-0.5 truncate" style={{ fontSize: '10px' }}>
                        {entry.post.title}
                      </p>
                    </button>
                  ))}
                  {dayEntries.length > 3 && (
                    <p className="text-xs text-muted mono px-1" style={{ fontSize: '9px' }}>
                      +{dayEntries.length - 3} weitere
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Entry detail panel */}
      {selectedEntry && (
        <div className="flex-shrink-0 border-t border-border bg-surface px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div
                className="w-1 h-full min-h-[40px] rounded flex-shrink-0"
                style={{ background: platformColors[selectedEntry.platform] ?? '#666' }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="mono text-xs font-700"
                    style={{ color: platformColors[selectedEntry.platform] ?? '#666' }}
                  >
                    {selectedEntry.platform}
                  </span>
                  <span className="text-xs text-muted">{selectedEntry.post.format}</span>
                  <span className="text-xs text-muted">·</span>
                  <span className="text-xs text-muted">{selectedEntry.post.tone}</span>
                </div>
                <p className="text-sm font-500 text-white truncate">{selectedEntry.post.title}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <Clock size={11} className="text-muted" />
                  <span className="mono text-xs text-muted">
                    {new Date(selectedEntry.date + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </span>
                  <span className="text-muted">·</span>
                  <input
                    type="time"
                    value={selectedEntry.time}
                    onChange={(e) => onUpdateTime(selectedEntry.id, e.target.value)}
                    className="mono text-xs text-accent bg-transparent border-none outline-none cursor-pointer hover:text-white transition-colors"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => onRemoveEntry(selectedEntry.id)}
                className="btn-ghost py-1.5 px-3 text-xs text-red-400 hover:text-red-300 border-red-900/40 hover:border-red-500/40"
              >
                <Trash2 size={12} />
                <span>Entfernen</span>
              </button>
              <button
                onClick={() => onSelectEntry(null)}
                className="btn-ghost py-1.5 px-3 text-xs"
              >
                <X size={12} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time picker modal */}
      {pendingDrop && (
        <TimePickerModal
          post={pendingDrop.post}
          date={pendingDrop.date}
          onConfirm={handleTimeConfirm}
          onCancel={() => setPendingDrop(null)}
        />
      )}
    </div>
  );
}
